import { useCallback, useEffect, useMemo, useState } from 'react';
import { sessionService } from '@/lib/sessionService';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import {
  PAYWALL_MESSAGES,
  SUBSCRIPTION_LIMITS,
  type PaywallBlockReason,
  type SubscriptionStatus,
} from '@/types/subscription';

export interface TrialProgress {
  hasTrial: boolean;
  isExpired: boolean;
  sessionsRemaining: number;
  daysRemaining: number;
  totalSessions: number;
  trialStartDate: string | null;
  trialEndDate: string | null;
}

interface EligibilityResult {
  canStart: boolean;
  reason: PaywallBlockReason | null;
  message: string | null;
}

type DailyUsage = number | null;

const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  'trial',
  'active',
  'past_due',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'unpaid',
  'free',
];

function normalizeStatus(status: string | null | undefined): SubscriptionStatus {
  if (!status) {
    return 'free';
  }

  // Trim whitespace and lowercase for robust matching
  const normalized = status.toLowerCase().trim();
  const match = SUBSCRIPTION_STATUSES.find((value) => value === normalized);
  return match ?? 'free';
}

function calculateDaysRemaining(trialEndDate: string | null): number {
  if (!trialEndDate) {
    return 0;
  }

  const end = new Date(trialEndDate);
  const now = new Date();

  // If trial already ended, return 0
  if (end.getTime() <= now.getTime()) {
    return 0;
  }

  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / msPerDay));
}

function getStartOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function useSubscription() {
  const { profile, loading, error } = useProfile();

  const status = useMemo(() => normalizeStatus(profile?.subscription_status), [profile?.subscription_status]);
  const isTrial = status === 'trial';
  const hasActiveSubscription = status === 'active';
  const isFree = status === 'free';

  // Premium features are available for trial or active subscriptions
  // Returns true during loading to avoid showing locked state while profile loads (optimistic UI)
  const canUsePremiumFeatures = loading || isTrial || hasActiveSubscription;

  return {
    status,
    isTrial,
    hasActiveSubscription,
    isFree,
    canUsePremiumFeatures,
    profile,
    loading,
    error,
  };
}

export function useTrialProgress(): TrialProgress {
  const { profile } = useProfile();

  const hasTrial = profile?.subscription_status === 'trial';
  const daysRemaining = calculateDaysRemaining(profile?.trial_end_date ?? null);

  const isExpired =
    !hasTrial ||
    daysRemaining <= 0 ||
    (profile?.trial_end_date ? new Date(profile.trial_end_date).getTime() <= Date.now() : false);

  return {
    hasTrial,
    isExpired,
    sessionsRemaining: 0, // Deprecated: No longer using session-based trial
    daysRemaining,
    totalSessions: 0, // Deprecated: No longer using session-based trial
    trialStartDate: profile?.trial_start_date ?? null,
    trialEndDate: profile?.trial_end_date ?? null,
  };
}

export function useCanStartSession() {
  const { user, isAuthenticated } = useAuth();
  const { status, profile, loading: profileLoading } = useSubscription();
  const trialProgress = useTrialProgress();
  const rawStatus = status;
  const effectiveStatus =
    trialProgress.hasTrial && trialProgress.isExpired ? 'free' : rawStatus;

  const [dailySessionsUsed, setDailySessionsUsed] = useState<DailyUsage>(null);
  const [allowanceLoading, setAllowanceLoading] = useState(false);
  const [lastFetchedDateKey, setLastFetchedDateKey] = useState<string | null>(null);

  const computeDailyUsage = useCallback(async (): Promise<number> => {
    if (!user?.id) {
      return 0;
    }

    const todayStart = getStartOfToday();
    const { data, error } = await sessionService.getUserSessions(user.id, 50);

    if (error || !data) {
      console.error('Failed to load session usage for paywall enforcement:', error);
      return 0;
    }

    const sessionsToday = data.filter((session) => {
      if (!session.started_at) {
        return false;
      }

      const sessionStart = new Date(session.started_at);
      return sessionStart >= todayStart;
    });

    return sessionsToday.length;
  }, [user?.id]);

  const refreshDailyUsage = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setDailySessionsUsed(null);
      setLastFetchedDateKey(null);
      return;
    }

    if (effectiveStatus !== 'free') {
      setDailySessionsUsed(null);
      setLastFetchedDateKey(null);
      return;
    }

    setAllowanceLoading(true);
    const usage = await computeDailyUsage();
    setDailySessionsUsed(usage);
    setLastFetchedDateKey(getStartOfToday().toISOString());
    setAllowanceLoading(false);
  }, [computeDailyUsage, isAuthenticated, effectiveStatus, user?.id]);

  useEffect(() => {
    if (effectiveStatus !== 'free') {
      return;
    }

    const todayKey = getStartOfToday().toISOString();
    if (lastFetchedDateKey !== todayKey) {
      void refreshDailyUsage();
    }
  }, [lastFetchedDateKey, refreshDailyUsage, effectiveStatus]);

  const determineBlockReason = useCallback(
    (currentDailyUsage: DailyUsage): PaywallBlockReason | null => {
      if (!isAuthenticated || !user?.id) {
        return 'not_authenticated';
      }

      if (!profile) {
        return 'no_profile';
      }

      if (effectiveStatus === 'trial') {
        return null;
      }

      if (effectiveStatus === 'free') {
        if (currentDailyUsage === null) {
          return null;
        }

        if (currentDailyUsage >= SUBSCRIPTION_LIMITS.freeDailySessionLimit) {
          return 'daily_limit_reached';
        }
      }

      return null;
    },
    [isAuthenticated, profile, effectiveStatus, user?.id]
  );

  const blockReason = useMemo(
    () => determineBlockReason(dailySessionsUsed),
    [dailySessionsUsed, determineBlockReason]
  );
  const isLoading = profileLoading || allowanceLoading;
  const canStartSession = !isLoading && blockReason === null;
  const blockMessage = blockReason ? PAYWALL_MESSAGES[blockReason] : null;

  const checkEligibility = useCallback(async (): Promise<EligibilityResult> => {
    if (!isAuthenticated || !user?.id) {
      return {
        canStart: false,
        reason: 'not_authenticated',
        message: PAYWALL_MESSAGES.not_authenticated,
      };
    }

    if (!profile) {
      return {
        canStart: false,
        reason: 'no_profile',
        message: PAYWALL_MESSAGES.no_profile,
      };
    }

    if (effectiveStatus === 'trial') {
      return { canStart: true, reason: null, message: null };
    }

    if (effectiveStatus === 'free') {
      const usage = await computeDailyUsage();
      const limited = usage >= SUBSCRIPTION_LIMITS.freeDailySessionLimit;

      if (limited) {
        return {
          canStart: false,
          reason: 'daily_limit_reached',
          message: PAYWALL_MESSAGES.daily_limit_reached,
        };
      }

      setDailySessionsUsed(usage);
      return { canStart: true, reason: null, message: null };
    }

    // Active or other paid states
    return { canStart: true, reason: null, message: null };
  }, [computeDailyUsage, isAuthenticated, profile, effectiveStatus, user?.id]);

  return {
    canStartSession,
    blockReason,
    blockMessage,
    loading: isLoading,
    trialProgress,
    status: effectiveStatus,
    rawStatus,
    dailySessionsUsed,
    refreshDailyUsage,
    checkEligibility,
  };
}
